/**
 * Toast — Ephemeral notification shown at the bottom of the screen. Uses a
 * function-based API (not a React component you render in JSX).
 *
 * Toast Component Guidelines
 * - Use toasts for transient feedback the user doesn't need to act on (e.g. "Saved",
 *   "Copied to clipboard"). They auto-dismiss and shouldn't contain critical info.
 * - For persistent errors or validation messages, use inline `<FieldError>` or an
 *   alert banner instead — toasts disappear before the user may notice them.
 * - Choose the variant to match severity: `success` for confirmations, `error` for
 *   failures, `warning` for recoverable issues, `info` for neutral updates.
 * - Keep `title` under ~60 characters. Use `description` only when the title alone
 *   isn't enough context (e.g. "File exceeds 10MB limit").
 * - Use `action` sparingly — only when the user can take an immediate follow-up
 *   action like undo, retry, or navigate. Most toasts shouldn't have one.
 * - Use `onClick` to make the whole toast clickable (e.g. navigate to the
 *   source of a notification). Clicking dismisses the toast; the X button and
 *   `action` button still work independently.
 * - Overlong content is visually clamped (title ~3 lines, description ~20 lines)
 *   so a giant error/stack can't blow out the toast. If you need callers to get
 *   the full text, give them a Copy action that writes the untruncated value to
 *   the clipboard — the clamp only affects display.
 * @see https://sonner.emilkowal.ski Sonner documentation
 * @see https://ui.shadcn.com/docs/components/sonner shadcn/ui Sonner documentation
 */
import type { AlertVariant } from "@replohq/lattice/blocks/alert/Alert";

import React from "react";

import { AlertIcon } from "@replohq/lattice/blocks/alert/Alert";
import { Button } from "@replohq/lattice/components/button/Button";
import { cn } from "@replohq/lattice/utils/cn";
import { X } from "lucide-react";
import { isNotNullish } from "replo-utils/lib/misc";
import { toast as sonnerToast } from "sonner";

type ToastVariant = Extract<
  AlertVariant,
  "success" | "error" | "warning" | "info"
>;

interface ToastAction {
  label: string;
  onClick: () => void;
  dismissOnClick?: boolean;
}

interface ToastProps {
  id: string | number;
  variant: ToastVariant;
  title: string;
  description?: string;
  action?: ToastAction;
  onClick?: () => void;
}

function Toast({
  id,
  variant,
  title,
  description,
  action,
  onClick,
}: ToastProps) {
  const isTitleOnly = !description && !action;
  const handleClick = onClick
    ? () => {
        // NOTE (Burk, 2026-06-10): A click that ends a text selection
        // shouldn't trigger the action.
        if (window.getSelection()?.toString()) {
          return;
        }
        onClick();
        sonnerToast.dismiss(id);
      }
    : undefined;
  return (
    <div
      onClick={handleClick}
      className={cn(
        "flex w-full min-w-[268px] gap-3 rounded-xl border border-0.5 border-border bg-sheet text-default p-4 shadow-2xl",
        isTitleOnly ? "items-center" : "items-start max-w-[400px]",
        handleClick && "cursor-pointer",
      )}
    >
      <AlertIcon variant={variant} />

      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <span
            className={cn(
              "header-h5 break-words",
              isTitleOnly ? "truncate" : "line-clamp-3",
            )}
          >
            {title}
          </span>
          {description && (
            <span className="select-text whitespace-pre-wrap break-words text-sm text-muted line-clamp-[20]">
              {description}
            </span>
          )}
        </div>

        {action && (
          <Button
            variant="secondary"
            size="base"
            onClick={(event) => {
              event.stopPropagation();
              action.onClick();
              if (action.dismissOnClick !== false) {
                sonnerToast.dismiss(id);
              }
            }}
          >
            {action.label}
          </Button>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <Button
          icon
          size="sm"
          variant="tertiary"
          onClick={(event) => {
            event.stopPropagation();
            sonnerToast.dismiss(id);
          }}
        >
          <X />
        </Button>
      </div>
    </div>
  );
}

function showToast(
  variant: ToastVariant,
  options: {
    title: string;
    description?: string;
    action?: ToastAction;
    onClick?: () => void;
    duration?: number;
    // Sonner updates an existing toast in place when the same id is reused, so
    // callers can collapse a burst of identical toasts into one.
    id?: string | number;
  },
) {
  const { title, description, action, onClick, duration, id } = options;

  return sonnerToast.custom(
    (toastId) => (
      <Toast
        id={toastId}
        variant={variant}
        title={title}
        description={description}
        action={action}
        onClick={onClick}
      />
    ),
    {
      ...(isNotNullish(duration) ? { duration } : {}),
      ...(isNotNullish(id) ? { id } : {}),
    },
  );
}

interface ToastOptions {
  title: string;
  description?: string;
  action?: ToastAction;
  onClick?: () => void;
  duration?: number;
  id?: string | number;
}

const toast = Object.assign(
  (options: ToastOptions) => {
    return showToast("success", options);
  },
  {
    success: (options: ToastOptions) => showToast("success", options),
    error: (options: ToastOptions) => showToast("error", options),
    warning: (options: ToastOptions) => showToast("warning", options),
    info: (options: ToastOptions) => showToast("info", options),
    // Dismiss a specific toast by id (e.g. a persistent notice), or all toasts when omitted.
    dismiss: (id?: string | number) => sonnerToast.dismiss(id),
  },
);

export { toast };
